import React, { useState, useEffect } from 'react';
import { Trash2, Plus, Music } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { songService } from '../../../services/songService';
import Icon from '../../../components/AppIcon';

export const SongsSection = ({ musicalId, onChange, initialSongs = [] }) => {
  const [songs, setSongs] = useState([]);
  const [newSong, setNewSong] = useState({ title: '', act_number: 1, song_order: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load songs only once when component mounts with musicalId
  useEffect(() => {
    if (musicalId && initialSongs?.length === 0) {
      loadSongs();
    } else if (initialSongs?.length > 0) {
      // Use initial songs from parent and deduplicate by title
      const uniqueSongs = deduplicateSongs(initialSongs);
      setSongs(uniqueSongs);
    }
  }, [musicalId]);

  // Helper function to deduplicate songs by normalized title
  const deduplicateSongs = (songsList) => {
    const seen = new Map();
    const uniqueSongs = [];

    songsList?.forEach(song => {
      const normalizedTitle = song?.title?.toLowerCase()?.trim();
      if (!seen?.has(normalizedTitle)) {
        seen?.set(normalizedTitle, true);
        uniqueSongs?.push(song);
      }
    });

    return uniqueSongs;
  };

  const loadSongs = async () => {
    try {
      setLoading(true);
      const musicalSongs = await songService?.getSongsByMusicalId(musicalId);
      // Deduplicate songs before setting state
      const uniqueSongs = deduplicateSongs(musicalSongs || []);
      setSongs(uniqueSongs);
    } catch (err) {
      console.error('Error loading songs:', err);
      setError('Failed to load songs');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSong = () => {
    if (!newSong?.title?.trim()) {
      setError('Song title is required');
      return;
    }

    // Check for duplicate song titles in the current songs list
    const normalizedNewTitle = newSong?.title?.toLowerCase()?.trim();
    const isDuplicate = songs?.some(song => 
      song?.title?.toLowerCase()?.trim() === normalizedNewTitle
    );

    if (isDuplicate) {
      setError(`A song with the title "${newSong?.title?.trim()}" already exists in this musical. Please use a different title.`);
      return;
    }

    const songToAdd = {
      ...newSong,
      id: `temp_${Date.now()}`, // Temporary ID for UI
      title: newSong?.title?.trim(),
      act_number: parseInt(newSong?.act_number) || 1,
      song_order: parseInt(newSong?.song_order) || 1,
      musical_id: musicalId || null
    };

    const updatedSongs = [...songs, songToAdd];
    setSongs(updatedSongs);
    
    // Notify parent component
    if (onChange) {
      onChange(updatedSongs);
    }

    // Reset form
    setNewSong({ 
      title: '', 
      act_number: 1, 
      song_order: updatedSongs?.length + 1 
    });
    setError(null);
  };

  const handleRemoveSong = (index) => {
    const updatedSongs = songs?.filter((_, i) => i !== index);
    setSongs(updatedSongs);
    
    // Notify parent component
    if (onChange) {
      onChange(updatedSongs);
    }
  };

  const handleUpdateSong = (index, field, value) => {
    const updatedSongs = [...songs];
    updatedSongs[index] = {
      ...updatedSongs?.[index],
      [field]: field === 'title' ? value : parseInt(value) || 1
    };
    setSongs(updatedSongs);
    
    // Notify parent component
    if (onChange) {
      onChange(updatedSongs);
    }
  };

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Music" size={20} className="text-blue-200" />
          <h2 className="text-xl font-heading font-semibold text-white">Songs</h2>
        </div>
        <span className="text-sm text-blue-200">
          {songs?.length} {songs?.length === 1 ? 'song' : 'songs'}
        </span>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="mb-4">
        <p className="text-blue-100 text-sm">
          Add and manage songs for this musical. Specify the act number and order for each song.
        </p>
      </div>

      {/* Add New Song Form */}
      <div className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <Input
              label="Song Title"
              value={newSong?.title}
              onChange={(e) => setNewSong({ ...newSong, title: e?.target?.value })}
              placeholder="Enter song title"
              className="bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-xl"
            />
          </div>
          <div>
            <Input
              type="number"
              label="Act #"
              value={newSong?.act_number}
              onChange={(e) => setNewSong({ ...newSong, act_number: e?.target?.value })}
              min="1"
              className="bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-xl"
            />
          </div>
          <div>
            <Input
              type="number"
              label="Order"
              value={newSong?.song_order}
              onChange={(e) => setNewSong({ ...newSong, song_order: e?.target?.value })}
              min="1"
              className="bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-xl"
            />
          </div>
        </div>
        <div className="mt-4">
          <Button
            onClick={handleAddSong}
            className="w-full md:w-auto bg-white bg-opacity-20 text-white hover:bg-opacity-30 rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Song
          </Button>
        </div>
      </div>

      {/* Songs List */}
      {loading ? (
        <div className="text-center py-8 text-blue-200">
          Loading songs...
        </div>
      ) : songs?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Music" size={48} className="text-blue-300 mx-auto mb-3 opacity-50" />
          <p className="text-blue-200 mb-4">No songs added yet. Add your first song above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {songs?.map((song, index) => (
            <div
              key={song?.id || index}
              className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl p-4 hover:bg-opacity-10 transition-all duration-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div className="md:col-span-2">
                  <Input
                    label="Song Title"
                    value={song?.title}
                    onChange={(e) => handleUpdateSong(index, 'title', e?.target?.value)}
                    className="bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-xl"
                  />
                </div>
                <div>
                  <Input
                    type="number"
                    label="Act #"
                    value={song?.act_number}
                    onChange={(e) => handleUpdateSong(index, 'act_number', e?.target?.value)}
                    min="1"
                    className="bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-xl"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Input
                      type="number"
                      label="Order"
                      value={song?.song_order}
                      onChange={(e) => handleUpdateSong(index, 'song_order', e?.target?.value)}
                      min="1"
                      className="bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-xl"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveSong(index)}
                    className="p-2 rounded-lg transition-colors flex-shrink-0 text-red-300 hover:text-red-100 hover:bg-red-500 hover:bg-opacity-20"
                    aria-label="Remove song"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};